import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { ResponsiveImageResource, UserProfile } from '@app/core/models';
import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { ModalManagerService } from '@app/core/services/modal-manager';
import { NavDataService } from '@app/core/services/nav-data/nav-data.service';
import { RolesService } from '@app/core/services/roles/roles.service';
import { DrupalConstants } from '@makiwin/ngx-drupal8-rest';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-profile-progress',
  templateUrl: './profile-progress.component.html',
  styleUrls: ['./profile-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileProgressComponent {
  schoolDetail: any;
  userPicture?: ResponsiveImageResource;
  isSchool = false;
  protected showButton = signal<boolean>(false);
  protected needToShowD7Profile = signal<boolean>(false);
  protected authSessionService: AuthSessionService = inject(AuthSessionService);
  protected userProfile$: Observable<UserProfile | null> = this.authSessionService.currentUserProfile$.pipe(
    tap((userProfile) => {
      if (!userProfile) {
        this._resetData();
        return;
      }
      this.isSchool = this.rolesService.userHasRole('school', true);
      this.userPicture = userProfile?.user_picture_multiple_json;
      this.showButton.set(!(this.isSchool && this.rolesService.userHasRole('pending', true)));
      this.needToShowD7Profile.set(this.isSchool && this.rolesService.userHasRole('expired', true));
    })
  );
  protected modalManagerService: ModalManagerService = inject(ModalManagerService);
  public navDataService: NavDataService = inject(NavDataService);
  public rolesService: RolesService = inject(RolesService);

  constructor() {}

  /**
   * Navigates the user to the appropriate profile page and closes the side menu.
   *
   * This method performs the following actions:
   * 1. Retrieves the current user profile from the authentication session service.
   * 2. Determines if the user is associated with a school.
   * 3. Toggles the side menu.
   * 4. If the user needs to update their D7 profile, navigates to the profile edit page.
   * 5. If the user's profile is 100% complete, navigates to the user's profile page or school page.
   * 6. If the profile is not complete, navigates to the profile completion page.
   * 7. Sets the navigation extras to activate the missing info tab if certain profile fields are present.
   * 8. Navigates to the root path and then to the appropriate profile path after a short delay.
   *
   * @returns {void}
   */
  navigateAndCloseMenu(): void {
    const profile: any = this.authSessionService.currentUserProfile;
    const isSchool = profile?.school !== '';

    this.navDataService.toggleSideMenu();
    if (this.needToShowD7Profile()) {
      this.navDataService.navigate(`/user/${DrupalConstants.Connection.current_user.uid}/edit`, true);
      return;
    }
    if (profile?.profile_completeness === '100%') {
      this.navDataService.navigate(
        isSchool ? profile?.school['url_alias'] : '/members/' + DrupalConstants.Connection.current_user.uid
      );
      return;
    }
    // compelete my profile
    let path = '/profile-old';
    if (isSchool) {
      path = path + '/school';
    } else {
      path = path + '/user';
    }
    const extras: NavigationExtras = {};
    // WEB-2950 active missing info tab
    if (
      profile?.user_picture_multiple_json &&
      profile?.field_first_name &&
      profile?.field_surname &&
      profile?.field_birth_date &&
      profile?.field_gender
    ) {
      extras.fragment = 'location';
    }
    this.navDataService.navigate('/');
    setTimeout(() => {
      this.navDataService.navigate(path, false, extras);
    }, 100);
  }

  private _resetData(): void {
    this.isSchool = false;
    this.userPicture = undefined;
    this.schoolDetail = null;
  }
}
